import { useState, useEffect, useMemo } from 'react';
import type { Dashboard as DashboardType, DashboardOpenEditorEvent } from '@/types/dashboard';
import { fetchDashboard } from '@/utils/api';
import { Dashboard } from './Dashboard';
import { EntitySelector } from './EntitySelector';
import { useEntityContext } from '@/hooks/useEntityContext';

export function DashboardEditorOverlay() {
  const [editorData, setEditorData] = useState<{
    uuid: string;
    sourceKey: string;
  } | null>(null);
  const [dashboardConfig, setDashboardConfig] = useState<DashboardType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDashboardDirty, setIsDashboardDirty] = useState(false);

  const {
    contextKey,
    selectedEntity,
    availableEntities,
    isLoadingEntities,
    selectEntity,
  } = useEntityContext(editorData?.sourceKey ?? '');

  const isGenericSourceKey = useMemo(() => {
    if (!editorData?.sourceKey) return false;
    return editorData.sourceKey.split(':').length === 2;
  }, [editorData?.sourceKey]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<DashboardOpenEditorEvent>).detail;
      if (isLoading) return;

      setEditorData(detail);
      setIsLoading(true);
      setError(null);

      fetchDashboard(detail.uuid)
        .then(data => {
          setDashboardConfig(data);
          setIsLoading(false);
        })
        .catch(err => {
          const msg = err instanceof Error ? err.message : 'Failed to load dashboard';
          setError(msg.includes('404') ? 'Дашборд не найден' : msg);
          setIsLoading(false);
        });
    };

    window.addEventListener('dashboard:open-editor', handler);
    return () => window.removeEventListener('dashboard:open-editor', handler);
  }, [isLoading]);

  const handleClose = () => {
    if (isDashboardDirty) {
      const confirmed = window.confirm('У вас есть несохранённые изменения. Вы уверены, что хотите закрыть?');
      if (!confirmed) {
        return;
      }
    }
    window.dispatchEvent(new CustomEvent('dashboard:refresh-list'));
    window.dispatchEvent(new CustomEvent('dashboard:show-list'));
    setEditorData(null);
    setDashboardConfig(null);
    setIsLoading(false);
    setError(null);
  };

  if (!editorData) return null;

  return (
    <div className="dashboard-editor-overlay position-fixed top-0 start-0 w-100 h-100 bg-white" style={{ zIndex: 1050 }}>
      <div className="d-flex align-items-center gap-3 px-4 py-2 border-bottom">
        <button
          onClick={handleClose}
          className="btn btn-outline-secondary btn-sm"
        >
          Назад к списку
        </button>
        <h1 className="h5 mb-0">
          {dashboardConfig?.title || 'Редактирование дашборда'}
        </h1>
        {isGenericSourceKey && (
          <div className="d-flex align-items-center gap-2 ms-auto">
            <span className="text-muted small">Контекст:</span>
            <EntitySelector
              entities={availableEntities}
              selectedEntity={selectedEntity}
              onSelect={selectEntity}
              isLoading={isLoadingEntities}
            />
          </div>
        )}
        {!isGenericSourceKey && (
          <span className="badge bg-info ms-auto">{editorData.sourceKey}</span>
        )}
      </div>

      {isLoading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
          <p className="mt-3">Загрузка...</p>
        </div>
      )}

      {error && (
        <div className="alert alert-danger m-4" role="alert">
          {error}
        </div>
      )}

      {!isLoading && !error && dashboardConfig && (
        <Dashboard
          initialConfig={dashboardConfig}
          isEditable={true}
          contextKey={contextKey}
          onDirtyChange={setIsDashboardDirty}
        />
      )}
    </div>
  );
}